import api from '.';
import {
  OrganiseDocument,
  OrganiseDocumentWithDownloadURL,
  OrganiseDocumentTag,
  RequestDocumentUploadURLRequest,
  RequestDocumentUploadURLResponse,
  ConfirmDocumentUploadRequest,
  KeepLiveRecordingRequest,
  KeepLiveRecordingResponse,
  LinkDocumentToEntryRequest,
  UpdateDocumentRequest,
  UpdateDocumentResponse,
} from '@karehero/models';

export interface ListOrganiseDocumentsParams {
  careCircleId: string;
  includeTags?: string[];
  excludeTags?: string[];
}

export const organiseDocumentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listOrganiseDocuments: builder.query<
      OrganiseDocument[],
      ListOrganiseDocumentsParams
    >({
      query: ({ careCircleId, includeTags, excludeTags }) => {
        const params = new URLSearchParams();
        if (includeTags?.length) {
          params.set('includeTags', includeTags.join(','));
        }
        if (excludeTags?.length) {
          params.set('excludeTags', excludeTags.join(','));
        }
        const queryString = params.toString();
        return `/care-circle/${careCircleId}/organise-document${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (_result, _error, { careCircleId }) => [
        { type: 'OrganiseDocument', id: careCircleId },
      ],
    }),
    listOrganiseDocumentTags: builder.query<OrganiseDocumentTag[], string>({
      query: (careCircleId) =>
        `/care-circle/${careCircleId}/organise-document/tags`,
      providesTags: ['OrganiseDocumentTags'],
    }),
    getOrganiseDocument: builder.query<
      OrganiseDocumentWithDownloadURL,
      { careCircleId: string; documentId: string }
    >({
      query: ({ careCircleId, documentId }) =>
        `/care-circle/${careCircleId}/organise-document/${documentId}`,
      providesTags: (_result, _error, { documentId }) => [
        { type: 'OrganiseDocument', id: documentId },
      ],
    }),
    requestOrganiseDocumentUploadURL: builder.mutation<
      RequestDocumentUploadURLResponse,
      { careCircleId: string; body: RequestDocumentUploadURLRequest }
    >({
      query: ({ careCircleId, body }) => ({
        url: `/care-circle/${careCircleId}/organise-document`,
        method: 'POST',
        body,
      }),
    }),
    confirmOrganiseDocumentUpload: builder.mutation<
      void,
      { careCircleId: string; body: ConfirmDocumentUploadRequest }
    >({
      query: ({ careCircleId, body }) => ({
        url: `/care-circle/${careCircleId}/organise-document/confirm`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { careCircleId }) => [
        { type: 'OrganiseDocument', id: careCircleId },
      ],
    }),
    keepLiveRecording: builder.mutation<
      KeepLiveRecordingResponse,
      { careCircleId: string; body: KeepLiveRecordingRequest }
    >({
      query: ({ careCircleId, body }) => ({
        url: `/care-circle/${careCircleId}/organise-document/live-recording`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { careCircleId }) => [
        { type: 'OrganiseDocument', id: careCircleId },
      ],
    }),
    linkOrganiseDocumentToEntry: builder.mutation<
      void,
      {
        careCircleId: string;
        documentId: string;
        body: LinkDocumentToEntryRequest;
      }
    >({
      query: ({ careCircleId, documentId, body }) => ({
        url: `/care-circle/${careCircleId}/organise-document/${documentId}/link`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { careCircleId, documentId }) => [
        { type: 'OrganiseDocument', id: careCircleId },
        { type: 'OrganiseDocument', id: documentId },
        { type: 'Organise', id: 'LIST' },
      ],
    }),
    deleteOrganiseDocument: builder.mutation<
      void,
      { careCircleId: string; documentId: string }
    >({
      query: ({ careCircleId, documentId }) => ({
        url: `/care-circle/${careCircleId}/organise-document/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { careCircleId }) => [
        { type: 'OrganiseDocument', id: careCircleId },
      ],
    }),
    updateOrganiseDocument: builder.mutation<
      UpdateDocumentResponse,
      { careCircleId: string; documentId: string; body: UpdateDocumentRequest }
    >({
      query: ({ careCircleId, documentId, body }) => ({
        url: `/care-circle/${careCircleId}/organise-document/${documentId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { careCircleId, documentId }) => [
        { type: 'OrganiseDocument', id: careCircleId },
        { type: 'OrganiseDocument', id: documentId },
      ],
    }),
  }),
});

export const {
  useListOrganiseDocumentsQuery,
  useListOrganiseDocumentTagsQuery,
  useGetOrganiseDocumentQuery,
  useLazyGetOrganiseDocumentQuery,
  useRequestOrganiseDocumentUploadURLMutation,
  useConfirmOrganiseDocumentUploadMutation,
  useKeepLiveRecordingMutation,
  useLinkOrganiseDocumentToEntryMutation,
  useDeleteOrganiseDocumentMutation,
  useUpdateOrganiseDocumentMutation,
} = organiseDocumentApi;

export default organiseDocumentApi;
